import { Component, OnDestroy, OnInit } from '@angular/core';
import { AtlasBaseMapComponent } from '../atlas-base-map.component';
import { HttpClient } from '@angular/common/http';
import { BootstrapMediaService } from 'src/app/services/bootstrap-media.service';
import { TranslationHelperService } from 'src/app/services/translations/translation-helper.service';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { FIX_WORKSPACES, POINTS_LABEL_ATLAS_FIX } from 'src/assets/constants/constants';
import GeoJSON from 'ol/format/GeoJSON';
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { unsubscribe } from 'src/app/utils/helpers';
import { AtlasFixFilterService } from 'src/app/services/filter/atlas-fix-filter.service';
import { PointDetails } from 'src/app/models/pointDetails';
import { forkJoin, map, tap } from 'rxjs';
import MapBrowserEvent from 'ol/MapBrowserEvent';
import { GeoserverAPIService } from 'src/app/services/geoserver/geoserver-api.service';
import { AreaLevel } from 'src/app/models/domain/arealevel-enum';

@Component({
    selector: 'app-atlas-fix-map',
    templateUrl: './atlas-fix-map.component.html',
    styleUrls: ['./atlas-fix-map.component.scss'],
})
export class AtlasFixMapComponent extends AtlasBaseMapComponent implements OnInit, OnDestroy {
    isPoint: boolean = false;

    public readonly LABEL_ATLASFIX_POINTS = POINTS_LABEL_ATLAS_FIX;
    get pointDetail(): PointDetails {
        return this.atlasFixFilterService.atlasFixService.pointDetails;
    }

    constructor(
        public atlasFixFilterService: AtlasFixFilterService,
        protected http: HttpClient,
        protected breakpointObserver: BootstrapMediaService,
        protected translationHelper: TranslationHelperService,
        public olMapService: OLMapService,
        private geoserverService: GeoserverAPIService
    ) {
        super(http, breakpointObserver, translationHelper, olMapService);
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.atlasFixFilterService.atlasFixService.polygonSubject.subscribe((val) => {
                if (!val) {
                    return;
                }
                this.atlasFixFilterService.setQueryParams();
                this.atlasFixFilterService.atlasFixService.setDefaultFilter();
                this.loadLayer();
                this.loadPoints();
            })
        );
        this.atlasFixFilterService.atlasFixService.getLegends('fix-legend');
        this.atlasFixFilterService.atlasFixService.getFilter('fix-filter');
        this.atlasFixFilterService.atlasFixService.getDetails('fix-detail');
        this.mapOperations();
    }

    mapOperations(): void {
        this.atlasFixFilterService.atlasFixService.eventsKey = this.olMapService.map.on('singleclick', (event: MapBrowserEvent<MouseEvent>) => {
            const joined = this.atlasFixFilterService.atlasFixService.mapOperations(event).filter((j) => j.layer && j.featureCollection);
            const featureRequests = joined.filter((j) => j.layer && j.featureCollection).map((j) => j.featureCollection);

            this.subscriptions.push(
                forkJoin(featureRequests).subscribe((featureCollections: GeoJSON[]) => {
                    const format = new GeoJSON();

                    const parsed = featureCollections.map((fc, i) => ({
                        layer: joined[i].layer,
                        features: format.readFeatures(fc),
                    }));

                    const statSec = parsed.find((p) => p.layer === AreaLevel.STATISTICAL_SECTOR);
                    const municipality = parsed.find((p) => p.layer === AreaLevel.MUNICIPALITY);
                    const province = parsed.find((p) => p.layer === AreaLevel.PROVINCE);
                    if (statSec && statSec.features.length == 0) {
                        return;
                    }
                    if (statSec?.features?.length && municipality?.features?.length) {
                        const statSecFeature = statSec.features[0];
                        const municipalityFeature = municipality.features[0];

                        if (province.features[0].get('Name') == 'Région de Bruxelles-Capitale') {
                            municipalityFeature.setProperties({
                                Name: `${municipalityFeature.get('Name') + ' | ' + municipalityFeature.get('alternative_name')}`,
                            });
                        } else {
                            municipalityFeature.setProperties({
                                Name: municipalityFeature.get('Name'),
                            });
                        }
                        this.updatePointDetails([statSecFeature]);
                        this.updateStatSecDetails([statSecFeature, municipalityFeature]);
                        this.addVectorLayer(statSecFeature, format);
                        this.addVectorLayer(municipalityFeature, format, true);
                    } else {
                        const allFeatures = parsed.map((p) => p.features).reduce((acc, val) => acc.concat(val), []);
                        if (allFeatures.length > 0) {
                            this.updatePointDetails(allFeatures);
                            this.updateDetails(allFeatures[0]);
                            this.addVectorLayer(allFeatures[0], format);
                        }
                    }
                })
            );
        });
    }

    getLayerDetails(feature: Feature<Geometry>): void {
        this.atlasFixFilterService.atlasFixService.getLayerDetails(feature);
    }

    getLayerName(): string {
        return this.atlasFixFilterService.atlasFixService.getLayerName();
    }
    getStyleName(): string {
        return this.atlasFixFilterService.atlasFixService.getStyleName();
    }
    loadLayer(): void {
        this.atlasFixFilterService.atlasFixService.loadLayer();
    }
    loadPoints(): void {
        this.atlasFixFilterService.atlasFixService.loadPoint(null);
    }
    setFilter(): void {
        this.atlasFixFilterService.atlasFixService.setFilter();
    }
    updateDetails(feature: Feature<Geometry>): void {
        this.atlasFixFilterService.atlasFixService.updateDetails(feature);
    }

    updateStatSecDetails(features: Feature<Geometry>[]): void {
        this.atlasFixFilterService.atlasFixService.updateStatSecDetails(features);
    }

    updatePointDetails(features: Feature<Geometry>[]): void {
        this.atlasFixFilterService.atlasFixService.updatePointDetails(features);
    }

    addVectorLayer(feature: Feature<Geometry>, format: GeoJSON, blackBoundries?: boolean) {
        this.atlasFixFilterService.atlasFixService.addVectorLayer(feature, format, blackBoundries);
    }
    ngOnDestroy(): void {
        this.atlasFixFilterService.atlasFixService.mapOperationsUn();
        unsubscribe(this.subscriptions);
    }
}
